import React, { useState } from 'react';
import { Shield, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import LiabilityWaiverModal from './LiabilityWaiverModal';
import { getRegistrationFeeLabel } from '../../utils/helpers';

const PlayerRegistrationForm = ({ clubName, clubCode, clubLogo, clubInfo, user, onSubmit, isSubmitting, onCancel }) => {
  // Debug: Log the club info
  console.log('🔍 PlayerRegistrationForm - clubName:', clubName);
  console.log('🔍 PlayerRegistrationForm - clubCode:', clubCode);
  console.log('🔍 PlayerRegistrationForm - clubLogo:', clubLogo);
  
  // Player Information - pre-fill from user data but allow editing
  const [fullName, setFullName] = useState(user?.name || user?.displayName || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [agreeWhatsAppGroups, setAgreeWhatsAppGroups] = useState(false);
  
  // Cascadia Willow Club specific check - ONLY by name
  const isCascadiaWillowClub = 
    clubName === 'Cascadia Willow Club' || 
    clubName === 'CWC';
  
  console.log('🔍 isCascadiaWillowClub:', isCascadiaWillowClub);
  
  // Format Selection
  const [formats, setFormats] = useState({
    tennis: false,
    leather: false
  });
  
  // Dynamic Format Selection
  const [selectedFormatIds, setSelectedFormatIds] = useState({});
  
  // Liability Waiver
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [agreeWaiver, setAgreeWaiver] = useState(false);
  
  // Validation
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!agreeWhatsAppGroups) newErrors.agreeWhatsAppGroups = 'You must agree to be added to WhatsApp groups';
    
    const hasDynamicFormats = clubInfo?.registrationFormats && clubInfo.registrationFormats.length > 0;
    if (hasDynamicFormats) {
      if (!Object.values(selectedFormatIds).some(v => v)) newErrors.formats = 'Select at least one format';
    } else {
      if (!formats.tennis && !formats.leather) newErrors.formats = 'Select at least one format';
    }

    if (!agreeWaiver) newErrors.agreeWaiver = 'You must agree to the liability waiver';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    
    console.log('🔍 Form submitted');
    console.log('🔍 isCascadiaWillowClub:', isCascadiaWillowClub);
    console.log('🔍 phoneNumber:', phoneNumber);
    console.log('🔍 agreeWhatsAppGroups:', agreeWhatsAppGroups);
    console.log('🔍 formats:', formats);
    console.log('🔍 agreeWaiver:', agreeWaiver);
    
    if (!validateForm()) {
      console.log('❌ Form validation failed');
      console.log('🔍 Errors:', errors);
      return;
    }
    
    console.log('✅ Form validation passed, submitting...');
    
    const hasDynamicFormats = clubInfo?.registrationFormats && clubInfo.registrationFormats.length > 0;
    let selectedFormatsData = [];
    if (hasDynamicFormats) {
      selectedFormatsData = clubInfo.registrationFormats.filter(f => selectedFormatIds[f.id]);
    }

    const formData = {
      fullName,
      phoneNumber,
      whatsappNumber: whatsappNumber || phoneNumber,
      email,
      agreeWhatsAppGroups,
      formats: {
        tennis: formats.tennis,
        leather: formats.leather
      },
      selectedFormats: selectedFormatsData,
      agreeWaiver,
      submittedAt: new Date().toISOString()
    };
    
    onSubmit(formData);
  };

  const getLiabilityWaiverText = () => {
    if (clubInfo?.waiverText) {
      return clubInfo.waiverText;
    }

    return `In consideration of being part of ${clubName} and participating in activities of the club, I, the undersigned, acknowledge and agree as follows:

1. Assumption of Risk: I understand that participating in sports and recreational activities involves inherent risks, including but not limited to physical injury, mental stress, and financial loss. I voluntarily assume all such risks associated with my and my family's participation.

2. Release of Liability: I release ${clubName}, its officers, directors, employees, volunteers, and agents from any and all liability for any injuries, damages, or losses arising from my and/or my family's participation in the club's activities that are directly conducted by the club or where I and/or my family participate on behalf of, or as a player of, ${clubName}. This release includes claims related to negligence, breach of duty, or any other cause of action.

3. Responsibility for Personal Safety: I agree to take full responsibility for my own and/or my family's safety during activities organized by ${clubName} or where I and/or my family participate on behalf of, or as a player of, ${clubName}. I and my family will follow all safety guidelines, use appropriate protective gear, and exercise caution.

4. No Claims Against the Club: I waive any right to make any claims, demands, or legal actions against ${clubName}, its officers, directors, employees, volunteers, and agents - arising out of my and/or my family's participation in its activities or any activities where I and/or my family participate on behalf of, or as a player of, ${clubName}.

5. Binding Effect: This waiver and release shall be binding upon me, my heirs, executors, administrators, and assigns.`;
  };

  // Helper function to get Cascadia logo URL based on environment
  const getCascadiaLogoUrl = () => {
    // First priority: Use the club logo passed as prop
    if (clubLogo) {
      return clubLogo;
    }
    
    // Fallback to local public folder
    return '/cascadia-logo.jpg';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header with gradient background */}
          <div className="bg-gradient-to-r from-blue-900 to-cyan-600 p-8">
            <div className="text-center">
              {/* Cascadia Willow Club Logo - Show only for CWC clubs */}
              {isCascadiaWillowClub && (
                <div className="mb-6">
                  <div className="inline-block bg-white rounded-xl p-6 shadow-lg">
                    <img 
                      src={getCascadiaLogoUrl()}
                      alt="Cascadia Willow Club"
                      className="h-40 w-auto"
                      style={{ maxWidth: '320px', objectFit: 'contain' }}
                      onError={(e) => {
                        // If logo fails to load from Firebase, try local fallback
                        if (!e.target.dataset.retried) {
                          e.target.dataset.retried = 'true';
                          e.target.src = '/cascadia-logo.jpg';
                        } else {
                          // If both fail, show the actual logo design in CSS
                          e.target.style.display = 'none';
                          const fallback = e.target.nextSibling;
                          if (fallback) fallback.style.display = 'block';
                        }
                      }}
                    />
                    <div className="hidden" style={{ display: 'none' }}>
                      {/* Fallback to show cricket-themed design if image fails */}
                      <div className="flex flex-col items-center justify-center h-32">
                        <div className="text-4xl mb-2">🏏</div>
                        <div className="text-green-900 font-bold text-xl">CASCADIA</div>
                        <div className="text-amber-700 font-bold text-2xl">WILLOW CLUB</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Show Shield icon only for non-CWC clubs */}
              {!isCascadiaWillowClub && (
                <div className="inline-block p-3 bg-white/20 backdrop-blur rounded-full mb-4">
                  <Shield className="text-white" size={40} />
                </div>
              )}
              
              <h1 className="text-3xl font-bold text-white mb-2">
                Player Registration Form
              </h1>
              <p className="text-blue-100 text-lg">
                {clubName}
              </p>
              <p className="text-blue-200 text-sm mt-2">
                Complete this one-time registration to join the club
              </p>
            </div>
          </div>
          
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
            {/* Player Information */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  1
                </div>
                Player Information
              </h2>
              
              <div className="space-y-4">
                {/* Name - Pre-filled from signup */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                      submitted && errors.fullName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your full name"
                    required
                  />
                  {submitted && errors.fullName && (
                    <p className="text-red-600 text-sm mt-1">{errors.fullName}</p>
                  )}
                  {!submitted && (
                    <p className="text-xs text-gray-500 mt-1">You can edit your name if needed</p>
                  )}
                </div>

                {/* Email - Pre-filled from signup */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                    {email}
                    <span className="ml-2 text-xs text-green-600">(from your account)</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${
                      submitted && errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="(123) 456-7890"
                  />
                  {submitted && errors.phoneNumber && (
                    <p className="text-red-600 text-sm mt-1">{errors.phoneNumber}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Number
                  </label>
                  <input
                    type="tel"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="If different from phone number"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank if same as phone number</p>
                </div>

                <div className={`border rounded-lg p-4 ${
                  submitted && errors.agreeWhatsAppGroups ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
                }`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreeWhatsAppGroups}
                      onChange={(e) => setAgreeWhatsAppGroups(e.target.checked)}
                      className="mt-1"
                    />
                    <span className="text-sm text-gray-700">
                      I agree to be added to all {clubName} WhatsApp groups as deemed fit by management *
                    </span>
                  </label>
                  {submitted && errors.agreeWhatsAppGroups && (
                    <p className="text-red-600 text-sm mt-2">{errors.agreeWhatsAppGroups}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Format Selection */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  2
                </div>
                Format Commitment
              </h2>
              
              <div className={`space-y-3 border rounded-lg p-4 ${
                submitted && errors.formats ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  What formats are you committing to play? *
                </p>
                {clubInfo?.registrationFormats && clubInfo.registrationFormats.length > 0 ? (
                  <>
                    {clubInfo.registrationFormats.map((format) => (
                      <label key={format.id} className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={!!selectedFormatIds[format.id]}
                          onChange={(e) => setSelectedFormatIds({ ...selectedFormatIds, [format.id]: e.target.checked })}
                        />
                        <div>
                          <span className="font-medium text-gray-900">{format.label}</span>
                          <p className="text-xs text-gray-600">{getRegistrationFeeLabel(clubName)}: ${format.price}</p>
                        </div>
                      </label>
                    ))}
                    {submitted && errors.formats && (
                      <p className="text-red-600 text-sm mt-2">{errors.formats}</p>
                    )}
                  </>
                ) : (
                  <>
                    <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={formats.tennis}
                        onChange={(e) => setFormats({ ...formats, tennis: e.target.checked })}
                      />
                      <div>
                        <span className="font-medium text-gray-900">🎾 Tennis Ball</span>
                        <p className="text-xs text-gray-600">{getRegistrationFeeLabel(clubName)}: $125</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={formats.leather}
                        onChange={(e) => setFormats({ ...formats, leather: e.target.checked })}
                      />
                      <div>
                        <span className="font-medium text-gray-900">🏏 Leather Ball</span>
                        <p className="text-xs text-gray-600">{getRegistrationFeeLabel(clubName)}: $250</p>
                      </div>
                    </label>
                    {submitted && errors.formats && (
                      <p className="text-red-600 text-sm mt-2">{errors.formats}</p>
                    )}
                    
                    {/* Donation Amount Info */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-semibold text-blue-900 mb-1">💰 {getRegistrationFeeLabel(clubName)}:</p>
                      <ul className="text-xs text-blue-800 space-y-1">
                        <li>• Tennis only - $125</li>
                        <li>• Leather or Leather + Tennis - $250</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Liability Waiver */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  3
                </div>
                Liability Waiver and Release
              </h2>
              
              <div className={`border rounded-lg p-6 ${
                submitted && errors.agreeWaiver ? 'border-red-500 bg-red-50' : 
                agreeWaiver ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
              }`}>
                {!agreeWaiver ? (
                  <>
                    <div className="flex items-start gap-3 mb-4">
                      <AlertTriangle className="text-orange-600 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">Important Legal Agreement</h3>
                        <p className="text-sm text-gray-700 mb-4">
                          You must read and agree to the liability waiver to complete registration.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowWaiverModal(true)}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                    >
                      📄 Read and Sign Liability Waiver
                    </button>
                    
                    {submitted && errors.agreeWaiver && (
                      <p className="text-red-600 text-sm mt-4">{errors.agreeWaiver}</p>
                    )}
                  </>
                ) : (
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
                    <div className="flex-1">
                      <h3 className="font-bold text-green-900 mb-2">✓ Waiver Signed</h3>
                      <p className="text-sm text-green-700">
                        You have successfully read and signed the liability waiver. Your signed copy has been recorded.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-6 border-t">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex-1 px-6 py-3 rounded-lg font-semibold text-white ${
                  isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            </div>
          </form>
          </div>
        </div>
      </div>

      {/* Liability Waiver Modal */}
      {/* Enhanced Liability Waiver Modal with Scroll & Signature */}
      {showWaiverModal && (
        <LiabilityWaiverModal
          waiver={{
            version: isCascadiaWillowClub ? 'Cascadia_v1' : '1.0',
            content: getLiabilityWaiverText(),
            effectiveDate: new Date().toISOString()
          }}
          user={user}
          clubName={clubName}
          onAgree={() => {
            setAgreeWaiver(true);
            setShowWaiverModal(false);
          }}
        />
      )}
    </div>
  );
};

export default PlayerRegistrationForm;